import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import LocationMap from '../../search/v2/LocationMap';
import type { Supplier } from '../../../types/Supplier';
import type { PublicLocationVO } from '../../../types/model/valueObject/account/PublicLocationVO';

interface SupplierLocationMapProps {
  locations: PublicLocationVO[];
}

const toSupplier = (loc: PublicLocationVO): Supplier => ({
  id: loc.locationId,
  locationId: loc.locationId,
  locationName: loc.locationName,
  locationDescription: '',
  companyName: '',
  companyEmail: '',
  companyPhoneNumber: '',
  locationPhoneNumber: '',
  companyDescription: '',
  locationManagerName: '',
  locationManagerPhone: '',
  locationManagerEmail: '',
  addressLatitude: loc.addressLatitude,
  addressLongitude: loc.addressLongitude,
  city: loc.city,
  state: loc.state,
  pricePerDay: loc.pricePerDay,
  pricePerWeek: 0,
  pricePerMonth: loc.pricePerMonth,
  listingImageFileName: loc.listingImageFileName,
  supplierLegalAgreementFileName: '',
  equipmentTypes: loc.equipmentTypes,
  gallery: loc.galleryImageFileNames || [],
  equipmentTypesAll: null,
  features: loc.features,
  hasInstantApproval: loc.hasInstantApproval,
  minNumberOfSpaces: 0,
  minDuration: 0,
  useCalendarDays: false,
  managedSpaces: 0,
  totalNumberOfSpaces: 0,
  maxSpacesBooked: 0,
  visible: true,
  distance: '',
  overageRate: 0,
  currentInventoryCount: 0,
  commissionRate: 0,
  chargeOverages: false,
});

/**
 * Calculates the zoom level needed to fit a bounding box in the map viewport.
 * Uses a simple approximation based on latitude/longitude spans and the actual
 * container dimensions. This is not a true Mercator projection calculation.
 */
const calculateZoomToFitBounds = (
  lats: number[],
  lngs: number[],
  mapWidth: number,
  mapHeight: number,
): number => {
  const WORLD_SIZE = 256;
  const MAX_ZOOM = 15;
  const PADDING_FACTOR = 0.85; // leave some padding around the edges

  const latSpan = Math.max(...lats) - Math.min(...lats);
  const lngSpan = Math.max(...lngs) - Math.min(...lngs);

  if (latSpan === 0 && lngSpan === 0) return MAX_ZOOM;

  let zoomLat = 20;
  let zoomLng = 20;

  if (latSpan > 0) {
    const latRad = (latSpan * Math.PI) / 180;
    zoomLat = Math.log2((mapHeight * PADDING_FACTOR) / (WORLD_SIZE * latRad / Math.PI));
  }

  if (lngSpan > 0) {
    zoomLng = Math.log2((mapWidth * PADDING_FACTOR * 360) / (WORLD_SIZE * lngSpan));
  }

  return Math.min(Math.round(Math.min(zoomLat, zoomLng)), MAX_ZOOM);
};

const SupplierLocationMap = ({ locations }: SupplierLocationMapProps) => {
  const suppliers = useMemo(() => locations.map(toSupplier), [locations]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) {
        setContainerSize({ width, height });
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const validCoords = useMemo(() => {
    const coords: { lat: number; lng: number }[] = [];
    for (const loc of locations) {
      const lat = parseFloat(loc.addressLatitude);
      const lng = parseFloat(loc.addressLongitude);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        coords.push({ lat, lng });
      }
    }
    return coords;
  }, [locations]);

  const lats = useMemo(() => validCoords.map(c => c.lat), [validCoords]);
  const lngs = useMemo(() => validCoords.map(c => c.lng), [validCoords]);

  const defaultCenter = useMemo(() => {
    if (validCoords.length === 0) return undefined;
    return {
      lat: (Math.min(...lats) + Math.max(...lats)) / 2,
      lng: (Math.min(...lngs) + Math.max(...lngs)) / 2,
    };
  }, [validCoords, lats, lngs]);

  const calculateZoom = useCallback(() => {
    if (validCoords.length < 2 || !containerSize) return undefined;
    return calculateZoomToFitBounds(lats, lngs, containerSize.width, containerSize.height);
  }, [validCoords, lats, lngs, containerSize]);

  const defaultZoom = useMemo(() => calculateZoom(), [calculateZoom]);

  if (locations.length === 0) return null;

  return (
    <div ref={containerRef} style={{ height: '100%' }}>
      <LocationMap
        locations={suppliers}
        onLocationSelect={() => {}}
        listHover={null}
        defaultCenter={defaultCenter}
        defaultZoom={defaultZoom}
      />
    </div>
  );
};

export default SupplierLocationMap;
